/**
 * What a card's action row shows after a press (D354): every button answers. Useful and not
 * useful are toggles with a pressed state, pin fills, hide folds the card away and says so with
 * a way back, refresh spins and says "Refreshed". DOM-free, so the states are tested without
 * React.
 */
import type { FeedPrefs } from '../../sharedHome.js';
import { type FeedItem, ratingOf } from '../../sharedHome.js';

export type CardPress = 'useful' | 'not-useful' | 'pin' | 'unpin' | 'hide' | 'refreshing' | 'refreshed';

/** The line a press leaves on the card (and for a screen reader). */
export function pressWords(press: CardPress, wasOn = false): string {
  switch (press) {
    case 'useful':
      return wasOn ? 'No longer marked useful' : 'Marked useful: more like this';
    case 'not-useful':
      return wasOn ? 'No longer marked not useful' : 'Marked not useful: fewer like this';
    case 'pin':
      return 'Pinned to the front';
    case 'unpin':
      return 'Unpinned';
    case 'hide':
      return 'Hidden for a week, or until it changes';
    case 'refreshing':
      return 'Reading again…';
    case 'refreshed':
      return 'Refreshed';
  }
}

export interface CardButtonStates {
  useful: boolean;
  notUseful: boolean;
  pinned: boolean;
  refreshing: boolean;
}

/** The pressed state of each toggle, from the person's preferences. */
export function buttonStates(prefs: FeedPrefs, item: Pick<FeedItem, 'id' | 'kind'>, refreshing: boolean): CardButtonStates {
  const rating = ratingOf(prefs, item.kind);
  return { useful: rating === 'useful', notUseful: rating === 'not-useful', pinned: prefs.pinned.includes(item.id), refreshing };
}

/** What is said under the carousel after a hide, and for how long the way back stays. */
export const UNDO_MS = 8000;

export function hiddenWords(title: string): string {
  const short = title.length > 60 ? `${title.slice(0, 59).trimEnd()}…` : title;
  return `Hidden for a week: ${short}`;
}

/** How far a press must travel before it is a drag, not a click. */
export const DRAG_START_PX = 6;
/** How far (or how fast) a drag must go to move a card. */
const DRAG_MOVE_PX = 60;
const FLICK_PX_PER_MS = 0.5;

/** What a drag ends in: a card forward, a card back, or none. Pure, so it is tested. */
export function dragOutcome(dx: number, ms: number): 'next' | 'previous' | 'stay' {
  const speed = ms > 0 ? Math.abs(dx) / ms : 0;
  if (Math.abs(dx) < DRAG_START_PX) return 'stay';
  if (Math.abs(dx) >= DRAG_MOVE_PX || speed >= FLICK_PX_PER_MS) return dx < 0 ? 'next' : 'previous';
  return 'stay';
}
